// The organisation section's reads and writes (organisation-admin M1): the organisation, its members and its pending
// invitations, each fetched whole (an organisation has hundreds of members at most).
import { queryOptions } from "@tanstack/react-query";
import { apiClient, unwrap } from "../../api/client";
import type { components } from "../../api/generated/vision-api";
import type { Role } from "./roles";

export type Organisation = components["schemas"]["Organisation"];
export type OrganisationUpdate = components["schemas"]["OrganisationUpdate"];
export type Member = components["schemas"]["Member"];
export type Invitation = components["schemas"]["Invitation"];

const api = () => apiClient("customers");

async function allPages<T>(fetchPage: (cursor?: string) => Promise<{ items: T[]; next_cursor?: string | null }>) {
  const items: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await fetchPage(cursor);
    items.push(...page.items);
    cursor = page.next_cursor ?? undefined;
  } while (cursor);
  return items;
}

export const organisationQuery = (orgId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/organisation"],
    queryFn: () => unwrap(api().GET("/v1/organisation", {})),
  });

export const membersQuery = (orgId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/members"],
    queryFn: () =>
      allPages((cursor) => unwrap(api().GET("/v1/members", { params: { query: { cursor, limit: 500 } } }))),
  });

export const invitationsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/invitations"],
    queryFn: () =>
      allPages((cursor) => unwrap(api().GET("/v1/invitations", { params: { query: { cursor, limit: 500 } } }))),
  });

export function updateOrganisation(body: OrganisationUpdate) {
  return unwrap(api().PATCH("/v1/organisation", { body }));
}

export function changeRole(userId: string, role: Role) {
  return unwrap(api().PATCH("/v1/members/{user_id}", { params: { path: { user_id: userId } }, body: { role } }));
}

export function removeMember(userId: string) {
  return unwrap(api().DELETE("/v1/members/{user_id}", { params: { path: { user_id: userId } } }));
}

export function invite(email: string, role: Role) {
  return unwrap(api().POST("/v1/invitations", { body: { email, role, valid_days: 7 } }));
}

export function withdrawInvitation(id: string) {
  return unwrap(api().DELETE("/v1/invitations/{invitation_id}", { params: { path: { invitation_id: id } } }));
}

// Spaces and groups (organisation-admin M2, WEB-SPG). The lists themselves are the device list's spacesQuery and
// groupsQuery, so a change here shows in its filters too.
export type Space = components["schemas"]["Space"];
export type SpaceKind = components["schemas"]["SpaceKind"];
export type Group = components["schemas"]["Group"];

export const SPACE_KINDS: SpaceKind[] = ["farm", "field", "city", "district", "street", "home", "room", "custom"];

export function createSpace(name: string, kind: SpaceKind, parentId: string | null) {
  return unwrap(api().POST("/v1/spaces", { body: { name, kind, parent_id: parentId } }));
}

export function updateSpace(id: string, name: string, kind: SpaceKind) {
  return unwrap(api().PATCH("/v1/spaces/{space_id}", { params: { path: { space_id: id } }, body: { name, kind } }));
}

export function deleteSpace(id: string) {
  return unwrap(api().DELETE("/v1/spaces/{space_id}", { params: { path: { space_id: id } } }));
}

export function createGroup(name: string, kind: string) {
  return unwrap(api().POST("/v1/groups", { body: { name, kind } }));
}

export function updateGroup(id: string, name: string, kind: string) {
  return unwrap(api().PATCH("/v1/groups/{group_id}", { params: { path: { group_id: id } }, body: { name, kind } }));
}

export function deleteGroup(id: string) {
  return unwrap(api().DELETE("/v1/groups/{group_id}", { params: { path: { group_id: id } } }));
}

export function addToGroup(groupId: string, deviceId: string) {
  return unwrap(
    api().PUT("/v1/groups/{group_id}/devices/{device_id}", {
      params: { path: { group_id: groupId, device_id: deviceId } },
    }),
  );
}

export function removeFromGroup(groupId: string, deviceId: string) {
  return unwrap(
    api().DELETE("/v1/groups/{group_id}/devices/{device_id}", {
      params: { path: { group_id: groupId, device_id: deviceId } },
    }),
  );
}

/** Move a device to a space, or out of every space with null (needs `configure` on the device and the space). */
export function moveToSpace(deviceId: string, spaceId: string | null) {
  return unwrap(
    api().PATCH("/v1/devices/{device_id}", { params: { path: { device_id: deviceId } }, body: { space_id: spaceId } }),
  );
}

/**
 * The same change for many devices, one request each, so one refusal does not stop the rest (WEB-SPG-03). Returns
 * the devices that were refused, with why.
 */
export async function forEachDevice(
  ids: string[],
  change: (id: string) => Promise<unknown>,
): Promise<{ id: string; error: unknown }[]> {
  const failed: { id: string; error: unknown }[] = [];
  for (let i = 0; i < ids.length; i += 8) {
    const batch = ids.slice(i, i + 8);
    const results = await Promise.allSettled(batch.map(change));
    results.forEach((r, j) => {
      if (r.status === "rejected") failed.push({ id: batch[j] ?? "", error: r.reason });
    });
  }
  return failed;
}

// Grants (organisation-admin M3, WEB-GRT).
export type Grant = components["schemas"]["Grant"];
export type GrantCreate = components["schemas"]["GrantCreate"];
export type GrantUpdate = components["schemas"]["GrantUpdate"];
export type GrantChange = components["schemas"]["GrantChange"];

/** One member's grants, newest first (an Owner or Admin sees every grant, BE-AC-04). */
export const grantsQuery = (orgId: string, userId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/grants", userId],
    queryFn: () =>
      allPages((cursor) =>
        unwrap(api().GET("/v1/grants", { params: { query: { cursor, limit: 500, user_id: userId } } })),
      ),
  });

/** The grants on one place or group, for who has access to it (accounts-places-sharing CL-05). */
export const scopeGrantsQuery = (orgId: string, scopeKind: "space" | "group", scopeId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/grants", scopeKind, scopeId],
    queryFn: () =>
      allPages((cursor) =>
        unwrap(
          api().GET("/v1/grants", {
            params: { query: { cursor, limit: 500, scope_kind: scopeKind, scope_id: scopeId } },
          }),
        ),
      ),
  });

/** An invitation that carries its grants (P-59): "Share this place" for a new person (CL-06). */
export function inviteWithGrants(body: components["schemas"]["InvitationCreate"]) {
  return unwrap(api().POST("/v1/invitations", { body }));
}

export function createGrant(body: GrantCreate) {
  return unwrap(api().POST("/v1/grants", { body }));
}

export function updateGrant(id: string, body: GrantUpdate) {
  return unwrap(api().PATCH("/v1/grants/{grant_id}", { params: { path: { grant_id: id } }, body }));
}

export function revokeGrant(id: string) {
  return unwrap(api().DELETE("/v1/grants/{grant_id}", { params: { path: { grant_id: id } } }));
}

// Claim objections (claim-objections WEB-OBJ, P-191): the windows on this organisation's former devices.
export type ClaimObjection = components["schemas"]["ClaimObjection"];

export const claimObjectionsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/claim-objections"],
    queryFn: async () => (await unwrap(api().GET("/v1/claim-objections", {}))).items,
    staleTime: 60_000,
  });

export function decideClaimObjection(id: string, decision: "object" | "approve") {
  return unwrap(
    api().POST("/v1/claim-objections/{objection_id}/{decision}", {
      params: { path: { objection_id: id, decision } },
    }),
  );
}
